// Index-specific color ramps + per-series styling for charts.
//
// Vegetation indices (NDVI, NDRE, GNDVI, SAVI, MSAVI) use a red → yellow →
// green ramp — same semantics (low = stressed, high = vigorous). NDWI uses
// a diverging blue ramp because high NDWI = wet/healthy canopy moisture and
// low NDWI = dry stress — reading it on the same red-green scale would
// invert the intuition for a superintendent making irrigation calls.
//
// Expected input ranges:
//   ndvi/ndre/gndvi/savi/msavi: roughly [-0.2, 0.9]; we clamp [-0.1, 0.9]
//   ndwi: roughly [-0.5, 0.5]; we clamp [-0.4, 0.6]

import type { SatelliteIndex } from '@greeniq/shared';

export interface IndexMeta {
  label: string;
  color: string; // base series color for charts
  tagline: string;
}

export const INDEX_META: Record<SatelliteIndex, IndexMeta> = {
  ndvi: { label: 'NDVI', color: '#16a34a', tagline: 'Vigor' },
  ndre: { label: 'NDRE', color: '#0891b2', tagline: 'Chlorophyll / N' },
  gndvi: { label: 'GNDVI', color: '#15803d', tagline: 'High-biomass vigor' },
  savi: { label: 'SAVI', color: '#b45309', tagline: 'Soil-adjusted (L=0.5)' },
  msavi: { label: 'MSAVI', color: '#c2410c', tagline: 'Soil-adjusted (auto)' },
  ndwi: { label: 'NDWI', color: '#1d4ed8', tagline: 'Moisture' },
};

/** Plain-English "what it measures + when to use" text — surfaced in the Sat
 *  Mode description card so supers aren't left guessing at index acronyms.
 *  Deliberately one sentence each; extra nuance belongs in a help doc. */
export const INDEX_DESCRIPTIONS: Record<SatelliteIndex, string> = {
  ndvi: 'Overall canopy vigor. The workhorse — good first look, tends to saturate on lush summer turf.',
  ndwi: 'Canopy water content. High = moist, low = dry stress. Spots drought patches before they brown.',
  ndre: 'Red-edge vegetation. More sensitive than NDVI in dense canopies — best for catching early nitrogen stress.',
  gndvi: 'Green-band NDVI. Doesn\'t saturate as fast on thick fairways; use mid-season when NDVI tops out.',
  savi: 'Soil-adjusted vigor (L=0.5). Use on thin or stressed turf where visible soil throws off NDVI.',
  msavi: 'Self-tuning soil-adjusted vigor. Use when canopy density varies a lot across the course.',
};

/** Value stops to label on the Absolute-view legend ramp. Vegetation indices
 *  share the same clamp [-0.1, 0.9]; NDWI uses [-0.4, 0.6]. */
export const INDEX_VALUE_STOPS: Record<SatelliteIndex, Array<{ value: number; label: string }>> = {
  ndvi: [
    { value: 0.2, label: '0.2 stressed' },
    { value: 0.5, label: '0.5 moderate' },
    { value: 0.8, label: '0.8 vigor' },
  ],
  ndre: [
    { value: 0.1, label: '0.1 low' },
    { value: 0.3, label: '0.3 mid' },
    { value: 0.6, label: '0.6 high' },
  ],
  gndvi: [
    { value: 0.3, label: '0.3 low' },
    { value: 0.5, label: '0.5 mid' },
    { value: 0.75, label: '0.75 high' },
  ],
  savi: [
    { value: 0.2, label: '0.2 stressed' },
    { value: 0.5, label: '0.5 moderate' },
    { value: 0.8, label: '0.8 vigor' },
  ],
  msavi: [
    { value: 0.2, label: '0.2 stressed' },
    { value: 0.5, label: '0.5 moderate' },
    { value: 0.8, label: '0.8 vigor' },
  ],
  ndwi: [
    { value: -0.3, label: '-0.3 dry' },
    { value: 0.0, label: '0 neutral' },
    { value: 0.4, label: '0.4 saturated' },
  ],
};

export const NO_DATA_COLOR = '#9ca3af'; // neutral gray

/** Per-index `[min, max]` that matches what each index actually reads on
 *  turf — aligned to the INDEX_VALUE_STOPS legend. Using a single shared
 *  `[-0.1, 0.9]` for every vegetation index collapses NDRE (naturally ~0.1–0.6)
 *  and GNDVI (~0.3–0.75) into the same green tile as NDVI (~0.2–0.9), so
 *  flipping indices didn't visibly change the map. These ranges reproject
 *  each index's own dynamic range across the full color ramp. */
export const INDEX_COLOR_RANGE: Record<SatelliteIndex, readonly [number, number]> = {
  ndvi: [0.1, 0.85],
  ndre: [0.05, 0.65],
  gndvi: [0.25, 0.8],
  savi: [0.1, 0.8],
  msavi: [0.1, 0.85],
  ndwi: [-0.3, 0.5],
};

// Simple linear interpolation between two hex colors.
function lerpColor(from: string, to: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const fr = parseInt(from.slice(1, 3), 16);
  const fg = parseInt(from.slice(3, 5), 16);
  const fb = parseInt(from.slice(5, 7), 16);
  const tr = parseInt(to.slice(1, 3), 16);
  const tg = parseInt(to.slice(3, 5), 16);
  const tb = parseInt(to.slice(5, 7), 16);
  const r = Math.round(fr + (tr - fr) * clamp);
  const g = Math.round(fg + (tg - fg) * clamp);
  const b = Math.round(fb + (tb - fb) * clamp);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const VEG_STOPS = [
  { t: 0.0, color: '#d73027' }, // red — stressed / dormant
  { t: 0.33, color: '#fdae61' }, // orange
  { t: 0.5, color: '#fee08b' }, // yellow
  { t: 0.67, color: '#a6d96a' }, // light green
  { t: 1.0, color: '#1a9850' }, // deep green — vigor
];

const NDWI_STOPS = [
  { t: 0.0, color: '#d97706' }, // amber — dry canopy
  { t: 0.5, color: '#fde68a' }, // pale yellow — neutral
  { t: 0.75, color: '#60a5fa' }, // mid blue
  { t: 1.0, color: '#1e40af' }, // deep blue — saturated
];

function rampLookup(stops: typeof VEG_STOPS, t: number): string {
  const c = Math.max(0, Math.min(1, t));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!;
    const b = stops[i + 1]!;
    if (c >= a.t && c <= b.t) {
      const span = b.t - a.t;
      const local = span === 0 ? 0 : (c - a.t) / span;
      return lerpColor(a.color, b.color, local);
    }
  }
  return stops[stops.length - 1]!.color;
}

function normalize(value: number, min: number, max: number): number {
  if (max - min === 0) return 0;
  return (value - min) / (max - min);
}

export function colorForValue(index: SatelliteIndex, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return NO_DATA_COLOR;
  }
  const [min, max] = INDEX_COLOR_RANGE[index];
  const stops = index === 'ndwi' ? NDWI_STOPS : VEG_STOPS;
  return rampLookup(stops, normalize(value, min, max));
}

// Diverging ramp for z-score anomaly: negative = red (stress), 0 = neutral,
// positive = green (unusually healthy). NDWI uses the same ramp but inverted
// only if we decide a high NDWI (wetter than normal) is a problem — for v1
// we keep the same semantics: "healthy = green" on the map regardless of
// index.
const ANOMALY_STOPS = [
  { t: 0.0, color: '#b91c1c' }, // deep red at z = -3
  { t: 0.33, color: '#fca5a5' }, // light red at z = -1
  { t: 0.5, color: '#e5e7eb' }, // neutral gray at z = 0
  { t: 0.67, color: '#86efac' }, // light green at z = +1
  { t: 1.0, color: '#15803d' }, // deep green at z = +3
];

export function colorForZScore(z: number | null | undefined): string {
  if (z === null || z === undefined || !Number.isFinite(z)) {
    return NO_DATA_COLOR;
  }
  // Clip to [-3, 3] for coloring even though the service clips to [-5, 5].
  const clamped = Math.max(-3, Math.min(3, z));
  const t = (clamped + 3) / 6;
  return rampLookup(ANOMALY_STOPS, t);
}
